import i18n from "i18next";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faUndo } from "@fortawesome/pro-regular-svg-icons";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { DeliverySourcePickingApi } from "src/api/generated/erp/delivery/api/deliverySourcePickingApi.ts";
import { WarehousePickingObjectLocationView } from "src/api/generated/erp/db/types/tables/warehousePickingObjectLocationView.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { CustomerOrderLineReservationFormApi } from "src/api/generated/erp/sales/delivery/api/customerOrderLineReservationFormApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { ClientSideDataGridModel } from "../../../../../common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderLineUnreservingDataGridProps {
	customerOrderId: number;
	refreshRef?: RefreshableElementRef;
	onUnreservationCompleted?: () => Promise<unknown>;
}

export const CustomerOrderLineUnreservingDataGrid = ({
	customerOrderId,
	refreshRef,
	onUnreservationCompleted,
}: CustomerOrderLineUnreservingDataGridProps) => {
	const showInputDialog = useInputDialog();

	return (
		<ClientSideDataGridModel
			fetchData={() =>
				DeliverySourcePickingApi.getPickableLocationsForPackageSource({
					source: {
						type: "customerOrder",
						customerOrderId: customerOrderId,
					},
				})
			}
			initialParams={{}}
			gridId={"D4A8E2F16B3C9A71"}
			getRows={(data) => data}
			getRowId={(row) => row.warehousePickingObjectLocationId}
			refreshRef={refreshRef}
			render={({ dataGridProps, refreshData, onlySelectedRow }) => (
				<AavoDataGrid<WarehousePickingObjectLocationView>
					columns={[
						integerColumn({
							field: "objectLineNumber",
							headerName: i18n.t("line_number"),
						}),
						textColumn({
							field: "configurationOrPartDescription",
							headerName: i18n.t("part"),
							width: 300,
						}),
						textColumn({
							field: "objectMark",
							headerName: i18n.t("mark"),
							width: 200,
						}),
						floatColumn({
							field: "reservedQuantity",
							headerName: i18n.t("reserved_quantity"),
							width: 150,
						}),
						textColumn({
							field: "partUnit",
							headerName: i18n.t("unit"),
						}),
						textColumn({
							field: "locationCode",
							headerName: i18n.t("warehouse_location"),
						}),
					]}
					actionBarComponents={
						<AsyncButton
							label={i18n.t("cancel_reservation")}
							variant={"outlined"}
							icon={faUndo}
							disabled={
								!onlySelectedRow ? i18n.t("select_a_single_row")
								: onlySelectedRow.objectAcquisitionMethod !== "WAREHOUSE" ?
									i18n.t("reservation_can_be_reverted_only_for_lines_with_warehouse_acquisition")
								:	undefined
							}
							onClick={async () => {
								if (!onlySelectedRow) return;
								const revertQuantity = await showInputDialog({
									title: i18n.t("quantity_to_cancel"),
									type: "decimal",
									defaultValue: onlySelectedRow.reservedQuantity,
									required: true,
								});
								if (revertQuantity == undefined) return;
								await CustomerOrderLineReservationFormApi.createReservations({
									reservationRequests: [
										{
											customerOrderLineId: onlySelectedRow.objectId,
											partWarehouseLocationId: onlySelectedRow.partWarehouseLocationId,
											quantityInWarehouseUnit: -revertQuantity,
										},
									],
								});
								await refreshData();
								await onUnreservationCompleted?.();
							}}
						/>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.delivery.customerOrderLineUnreserving.grid"),
						data: { customerOrderId },
						selectedRowPathSegment: (row) => `${i18n.t("customer_order_line")} ${row.objectId}`,
						rowFields: [
							"warehousePickingObjectLocationId",
							"objectId",
							"objectLineNumber",
							"partWarehouseLocationId",
						],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
